/**
 * "Build Your Own Plan" pricing engine (#89).
 *
 * Pure function — no side effects, no API calls, no state. Given the config,
 * the buyer's selections, and a billing cycle, returns a full price breakdown.
 *
 * Order of operations (see docs/planning/pricing-calculator/01-pricing-config.md):
 *   1. Sum each component's monthly contribution → subtotal.
 *   2. Apply the minimum monthly floor.
 *   3. Apply the annual discount (annual cycle only) to the floored monthly rate.
 * The floor is applied before the discount so the discount comes off the real
 * (enforced) monthly rate, matching standard SaaS annual-billing behaviour.
 */
import type {
  PricingBillingCycle,
  LineItem,
  PricingComponent,
  PricingConfig,
  PricingResult,
  PricingSelection,
} from './pricing.types';

/** Round to 2 decimal places, avoiding binary-float drift (e.g. 0.1 + 0.2). */
function round2(value: number): number {
  return Math.round((value + Number.EPSILON) * 100) / 100;
}

function asCount(value: PricingSelection[string] | undefined, fallback: number): number {
  return typeof value === 'number' && Number.isFinite(value) ? value : fallback;
}

/**
 * Monthly amount + human-readable detail for a single component given the
 * current selection. Returns `null` for components that contribute nothing and
 * shouldn't appear as a line item (e.g. an unselected add-on).
 */
function lineItemFor(component: PricingComponent, selections: PricingSelection): LineItem | null {
  const selection = selections[component.slug];

  switch (component.type) {
    case 'base_fee':
      return {
        slug: component.slug,
        label: component.label,
        amount: round2(component.config.price),
      };

    case 'per_unit': {
      const { unitPrice, included } = component.config;
      const count = asCount(selection, included);
      const billable = Math.max(0, count - included);
      if (billable === 0) return null;
      return {
        slug: component.slug,
        label: component.label,
        amount: round2(billable * unitPrice),
        detail: `${billable} × $${unitPrice}`,
      };
    }

    case 'volume_tiered': {
      const count = asCount(selection, component.config.included);
      const { tiers } = component.config;
      let amount = 0;
      let billable = 0;
      for (let i = 0; i < tiers.length; i++) {
        const tier = tiers[i];
        if (count < tier.min) continue;
        // The last tier is open-ended: bill every unit at/above its min so
        // counts beyond the configured `max` are never silently un-billed
        // (an undercharge bug). Earlier tiers are capped at their `max`; the
        // Zod schema guarantees tiers are contiguous, so no unit is missed.
        const isLastTier = i === tiers.length - 1;
        const upper = isLastTier ? count : Math.min(count, tier.max);
        const unitsInTier = upper - tier.min + 1;
        if (unitsInTier <= 0) continue;
        amount += unitsInTier * tier.unitPrice;
        billable += unitsInTier;
      }
      amount = round2(amount);
      if (amount === 0) return null;
      return {
        slug: component.slug,
        label: component.label,
        amount,
        detail: `${billable} @ tiered`,
      };
    }

    case 'toggle':
    case 'toggle_integration': {
      if (selection !== true) return null;
      return {
        slug: component.slug,
        label: component.label,
        amount: round2(component.config.price),
      };
    }

    case 'select': {
      const value = typeof selection === 'string' ? selection : undefined;
      const option = component.config.options.find((o) => o.value === value);
      if (!option || option.price === 0) return null;
      return {
        slug: component.slug,
        label: component.label,
        amount: round2(option.price),
        detail: option.label,
      };
    }

    default: {
      // Exhaustiveness guard — a new component type must be handled above.
      const _never: never = component;
      return _never;
    }
  }
}

/**
 * Calculate the full price breakdown for a set of selections.
 *
 * @param config - The pricing config (tiers + priced components).
 * @param selections - Buyer's selections keyed by component slug.
 * @param billingCycle - `'monthly'` or `'annual'`.
 */
export function calculatePrice(
  config: PricingConfig,
  selections: PricingSelection,
  billingCycle: PricingBillingCycle
): PricingResult {
  const lineItems = config.components
    .map((component) => lineItemFor(component, selections))
    .filter((item): item is LineItem => item !== null);

  const subtotal = round2(lineItems.reduce((sum, item) => sum + item.amount, 0));

  // Enforce the monthly floor first; the annual discount then applies to it.
  const monthlyTotal = Math.max(subtotal, config.minimumMonthlyPrice);

  const isAnnual = billingCycle === 'annual';
  const discount = isAnnual ? round2((monthlyTotal * config.annualDiscountPercent) / 100) : 0;
  const total = round2(monthlyTotal - discount);
  const annualTotal = round2(total * 12);
  const savingsVsMonthly = round2(monthlyTotal * 12 - annualTotal);

  return { lineItems, subtotal, discount, total, annualTotal, savingsVsMonthly };
}
